import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner, Alert } from 'react-bootstrap';
import useToken from 'hooks/useToken';

const TabActividad = ({ rnc }) => {
    const [actividades, setActividades] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const { token } = useToken();

    useEffect(() => {
        if (rnc && rnc.idRepublicaDominicanaFacturacionExistencias) {
            cargarActividad();
        }
    }, [rnc]);

    const cargarActividad = async () => {
        setCargando(true);
        setError(null);

        try {
            const response = await fetch(
                `/api/FacturaRepublicaDominicana/ObtenerActividadRNC?_idRNC=${rnc.idRepublicaDominicanaFacturacionExistencias}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const responseType = await response.json();
                
                if (responseType.iResponseType === 1) {
                    const datos = JSON.parse(responseType.detalles);
                    
                    // Mapear los datos del backend al formato esperado por el frontend
                    const actividadesMapeadas = datos.map(item => ({
                        vTipoActividad: mapearTipoActividad(item.vMovement),
                        vTitulo: item.vMovement || 'Actividad',
                        vDescripcion: item.vDescription || '',
                        dFechaActividad: item.dCreatedDate,
                        vUsuario: item.vCreatedUser
                    }));
                    
                    setActividades(actividadesMapeadas);
                } else {
                    setError(responseType.vMessage);
                }
            } else {
                setError('Error al obtener actividad');
            }
        } catch (error) {
            console.error('Error al cargar actividad:', error);
            setError('Error al cargar actividad: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    const mapearTipoActividad = (movement) => {
        if (!movement) return 'DEFAULT';
        
        const movementUpper = movement.toUpperCase();
        
        if (movementUpper.includes('PLAN') || movementUpper.includes('ASSIGNED')) {
            return 'PLAN';
        }
        if (movementUpper.includes('FOLIO')) {
            return 'FOLIO';
        }
        if (movementUpper.includes('FACTURA') || movementUpper.includes('INVOICE')) {
            return 'FACTURA';
        }
        if (movementUpper.includes('UPDATE') || movementUpper.includes('ACTUALIZ')) {
            return 'ACTUALIZACION';
        }
        if (movementUpper.includes('CREATE') || movementUpper.includes('CREACI')) {
            return 'CREACION';
        }
        
        return 'DEFAULT';
    };

    const obtenerIconoActividad = (tipo) => {
        const iconos = {
            'FACTURA': 'file-invoice',
            'FOLIO': 'plus-circle',
            'PLAN': 'credit-card',
            'ACTUALIZACION': 'edit',
            'CREACION': 'plus-square',
            'DEFAULT': 'circle'
        };
        return iconos[tipo] || iconos['DEFAULT'];
    };

    const obtenerColorActividad = (tipo) => {
        const colores = {
            'FACTURA': 'success',
            'FOLIO': 'info',
            'PLAN': 'warning',
            'ACTUALIZACION': 'primary',
            'CREACION': 'secondary',
            'DEFAULT': 'secondary'
        };
        return colores[tipo] || colores['DEFAULT'];
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return 'No disponible';
        
        try {
            const date = new Date(fecha);
            const ahora = new Date();
            const diferencia = ahora - date;
            
            const minutos = Math.floor(diferencia / 60000);
            const horas = Math.floor(diferencia / 3600000);
            const dias = Math.floor(diferencia / 86400000);
            
            if (minutos < 60) {
                return `Hace ${minutos} minuto${minutos !== 1 ? 's' : ''}`;
            } else if (horas < 24) {
                return `Hace ${horas} hora${horas !== 1 ? 's' : ''}`;
            } else if (dias < 30) {
                return `Hace ${dias} día${dias !== 1 ? 's' : ''}`;
            } else {
                return date.toLocaleDateString('es-DO', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                });
            }
        } catch (error) {
            return 'Fecha inválida';
        }
    };

    if (!rnc) return null;

    if (cargando) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Cargando actividad...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="warning">
                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                {error}
            </Alert>
        );
    }

    if (!actividades || actividades.length === 0) {
        return (
            <Alert variant="info">
                <FontAwesomeIcon icon="info-circle" className="me-2" />
                No hay actividad registrada para este RNC
            </Alert>
        );
    }

    return (
        <div>
            <h6 className="text-muted mb-3">Actividad reciente</h6>
            
            <div className="timeline">
                {actividades.map((actividad, index) => (
                    <div 
                        key={index} 
                        className={`timeline-item mb-3 pb-3 ${index < actividades.length - 1 ? 'border-bottom' : ''}`}
                    >
                        <div className="d-flex">
                            <div className={`timeline-icon bg-${obtenerColorActividad(actividad.vTipoActividad)} text-white me-3`}>
                                <FontAwesomeIcon icon={obtenerIconoActividad(actividad.vTipoActividad)} />
                            </div>
                            <div className="flex-grow-1">
                                <div className="small text-muted">
                                    {formatearFecha(actividad.dFechaActividad)}
                                </div>
                                <div className="fw-semibold">{actividad.vTitulo}</div>
                                <div className="small text-muted">
                                    {actividad.vDescripcion}
                                </div>
                                {actividad.vUsuario && (
                                    <div className="small text-muted mt-1">
                                        <FontAwesomeIcon icon="user" className="me-1" />
                                        {actividad.vUsuario}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx="true">{`
                .timeline-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.875rem;
                    flex-shrink: 0;
                }
            `}</style>
        </div>
    );
};

TabActividad.propTypes = {
    rnc: PropTypes.object
};

export default TabActividad;
